import { contentItems, type ContentItem, type ContentType } from "@/mocks/content";

export const typeMeta: Record<
  ContentType,
  { label: string; short: string; path: string; icon: string; description: string }
> = {
  animation: {
    label: "动画剧情介绍",
    short: "剧情介绍",
    path: "/category/animation",
    icon: "ri-film-line",
    description:
      "按话数梳理「家庭教师」动画的关键剧情，包含概要、分集亮点与伏笔解析，帮助你看懂每一条暗线。",
  },
  character: {
    label: "角色分析",
    short: "角色分析",
    path: "/category/character",
    icon: "ri-user-heart-line",
    description:
      "从人物简介、性格特质到关键剧情，多维度解析主角、配角与反派，读懂角色背后的动机与成长。",
  },
  news: {
    label: "动漫资讯",
    short: "动漫资讯",
    path: "/category/news",
    icon: "ri-newspaper-line",
    description:
      "汇总「家庭教师」及相关动漫的最新动态、制作消息、活动资讯与访谈内容，第一时间掌握圈子风向。",
  },
};

const allTypes: ContentType[] = ["animation", "character", "news"];

export function isContentType(value: string): value is ContentType {
  return allTypes.includes(value as ContentType);
}

export function getAll(): ContentItem[] {
  return contentItems;
}

export function getByType(type: ContentType): ContentItem[] {
  return contentItems.filter((item) => item.type === type);
}

export function getById(type: ContentType, id: string): ContentItem | undefined {
  return contentItems.find((item) => item.type === type && item.id === id);
}

export function detailPath(item: ContentItem): string {
  return `/detail/${item.type}/${item.id}`;
}

export function formatDate(date: string): string {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}

export function shareTag(item: ContentItem): string {
  return `${item.type}-${item.id}`;
}

export function searchContent(keyword: string, type?: ContentType | "all"): ContentItem[] {
  const key = keyword.trim().toLowerCase();
  const pool = !type || type === "all" ? contentItems : contentItems.filter((i) => i.type === type);
  if (!key) return pool;
  return pool.filter((item) => {
    const haystack = [
      item.title,
      item.subtitle,
      item.summary,
      item.author,
      item.tags.join(" "),
      item.sections.map((s) => `${s.heading} ${s.body.join(" ")}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
}

function sharedTagScore(a: ContentItem, b: ContentItem): number {
  return a.tags.filter((tag) => b.tags.includes(tag)).length;
}

export function getRelated(item: ContentItem, limit = 3): ContentItem[] {
  const others = contentItems.filter((i) => i.id !== item.id);
  const sameType = others.filter((i) => i.type === item.type);
  const crossType = others.filter((i) => i.type !== item.type);

  const rank = (list: ContentItem[]) =>
    [...list].sort((a, b) => sharedTagScore(item, b) - sharedTagScore(item, a));

  const merged = [...rank(sameType), ...rank(crossType)];
  return merged.slice(0, limit);
}

export function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}